import * as THREE from 'three';

// portalMaterial — the portal's EFFECT STACK, as one material.
//
// Effects layer here by chaining inside a single fragment shader rather than by
// nesting real layers, because every effect in this stack is PER-PIXEL: each one
// only needs the pixel it is working on. Stacking them as separate meshes or
// render targets would cost a draw call and a full-screen buffer each, for an
// identical result.
//
// The line worth knowing: a NEIGHBOURHOOD effect — blur, bloom, glow, a
// convolved edge-soften — cannot join this chain. A fragment shader can't read
// what its neighbours produced, so those need a second pass: render the portal
// into a WebGLRenderTarget, then draw that texture through a blur shader. That
// costs an offscreen buffer per portal and isn't built here. Everything below is
// free by comparison.
//
// The chain, in order. Each step only ever REDUCES alpha, so they compose
// predictably and any one can be dialled to zero without disturbing the rest:
//
//   1. sample          the video frame
//   2. chroma key      background → alpha 0             (chroma mode only)
//   3. matte choke     shrink/soften the keyed edge      (kills the key fringe)
//   4. shape mask      rounded rect or ellipse           (a doorway, not a rect)
//   5. edge feather    fade out at the quad border       (no visible seam)
//   6. spill suppress  desaturate key bleed at the edge  (chroma mode only)
//   7. tint + opacity  world time-of-day, and the fade-in
//
// Steps 4 and 5 are the answer to "feather the composited layer": the mask is
// computed in ASPECT-CORRECTED space, so a wide quad feathers by the same
// physical distance on every side instead of smearing horizontally.

export const KEY_PRESETS = {
  green: '#00b140',    // broadcast chroma green — by far the strongest prior in any video model
  blue: '#0047bb',     // when the subject is green (foliage, neon) or blonde
  magenta: '#ff00ff',  // rarest in nature, but models render it least reliably
};

export const SHAPES = { rect: 0, ellipse: 1 };

const VERT = /* glsl */`
  varying vec2 vUv;
  varying float vFogDepth;
  void main() {
    vUv = uv;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    // Distance from the eye, for fog. A custom ShaderMaterial gets NO fog for
    // free — three only injects its fog chunks into its own materials — so the
    // portal sat outside the scene's air until this was carried through by hand.
    vFogDepth = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAG = /* glsl */`
  uniform sampler2D map;
  uniform float keyOn;        // 0 = show the video as-is (seed mode), 1 = key it
  uniform vec3  keyColor;
  uniform float similarity;   // sideways HUE tolerance (see the key below)
  uniform float satFloor;     // how much key-hue a pixel needs before it counts as background
  uniform float smoothness;
  uniform float spill;
  uniform float matteChoke;   // eat into the keyed edge — removes the key fringe
  uniform float matteFeather; // soften that edge
  // GARBAGE MATTE — a plain inset crop, discarded regardless of colour. Every
  // compositor has one, because a keyer can only remove what it recognises: a
  // generated backdrop that drifts dark and desaturated (a stray horizon line,
  // a ceiling edge) is deliberately PRESERVED by the saturation gate that stops
  // shadowed folds punching holes in the subject. Colour can't separate them —
  // position can.
  uniform vec4  crop;         // top, right, bottom, left — 0..1 of each side
  uniform float cropFeather;
  uniform float shape;        // 0 = rounded rect, 1 = ellipse
  uniform float cornerRadius; // rect only, 0..1 of the short side
  uniform float edgeFeather;  // border fade width, 0..1 of the short side
  uniform float quadAspect;   // width / height, so feather is even on all sides
  uniform float opacity;      // the portal's fade-in
  uniform vec3  tint;         // world time-of-day grade (hue + night dim)
  uniform vec3  fogColor;     // copied from the scene's own fog each frame
  uniform float fogNear;
  uniform float fogFar;
  uniform float fogAmount;    // 0 disables; 1 = exactly the scene's fog
  uniform float debugKey;     // 1 = render the matte itself, for tuning
  varying vec2 vUv;
  varying float vFogDepth;

  // RGB → the chroma half of YCbCr. Luma is discarded on purpose: a shadow on
  // the background must not read as a different colour.
  vec2 chroma(vec3 c) {
    return vec2(
      -0.168736 * c.r - 0.331264 * c.g + 0.5      * c.b,
       0.5      * c.r - 0.418688 * c.g - 0.081312 * c.b
    );
  }

  // Signed distance to a rounded box: negative inside, 0 on the edge.
  float sdRoundBox(vec2 p, vec2 b, float r) {
    vec2 q = abs(p) - b + r;
    return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
  }

  void main() {
    vec4 src = texture2D(map, vUv);

    // ── 2. key ────────────────────────────────────────────────────────────
    // NOT "distance from the key colour". That test fails on its own terms:
    // #00b140's chroma magnitude is only 0.328, so a NEUTRAL pixel — grey,
    // white, black, any shadow — sits ~0.328 away from green, right on top of a
    // sensible similarity threshold. Every shadowed fold in the subject then
    // keys out, which is exactly how a clean matte ends up full of holes and
    // then dissolves as the clip drifts darker.
    //
    // Grey is not green; it is colourLESS. So the test is split in two, and a
    // pixel must fail BOTH to be treated as background:
    //   along — how much of the key HUE it carries, scaled by the key's own
    //           saturation. Neutrals score ~0 and are therefore never keyed,
    //           no matter how dark they get.
    //   perp  — how far sideways off the key hue it sits. A red jacket scores
    //           high here and survives even where it is bright.
    float alpha = 1.0;
    if (keyOn > 0.5) {
      vec2 pc = chroma(src.rgb);
      vec2 kc = chroma(keyColor);
      float kcLen = max(length(kc), 1e-5);
      vec2 kdir = kc / kcLen;
      float along = dot(pc, kdir) / kcLen;                       // 1.0 = full key saturation
      float perp  = length(pc - dot(pc, kdir) * kdir) / kcLen;   // sideways hue error
      float soft  = max(smoothness, 1e-4);

      float hueMatch = 1.0 - smoothstep(similarity, similarity + soft, perp);
      float saturated = smoothstep(satFloor, satFloor + soft, along);
      alpha = 1.0 - hueMatch * saturated;

      // ── 3. matte choke: remap the alpha ramp inward. A generated "green
      //       screen" leaves a dirty transitional band; choking eats it, and
      //       feather sets how hard the resulting edge lands.
      alpha = smoothstep(matteChoke, matteChoke + max(matteFeather, 1e-4), alpha);
    }

    // ── 3b. garbage matte ─────────────────────────────────────────────────
    float cf = max(cropFeather, 1e-4);
    alpha *= smoothstep(crop.w, crop.w + cf, vUv.x);              // left
    alpha *= smoothstep(crop.y, crop.y + cf, 1.0 - vUv.x);        // right
    alpha *= smoothstep(crop.z, crop.z + cf, vUv.y);              // bottom
    alpha *= smoothstep(crop.x, crop.x + cf, 1.0 - vUv.y);        // top

    // ── 4/5. shape mask + edge feather, in aspect-corrected space ─────────
    // p is the pixel's position in units where 1.0 = half the SHORT side, so a
    // feather of 0.1 is the same physical distance top/bottom and left/right.
    vec2 p = (vUv - 0.5) * vec2(quadAspect, 1.0) * 2.0;
    vec2 b = vec2(quadAspect, 1.0);
    float feather = max(edgeFeather, 1e-4);
    float mask;
    if (shape > 0.5) {
      float e = length(p / b);                        // 1.0 at the ellipse edge
      mask = 1.0 - smoothstep(1.0 - feather, 1.0, e);
    } else {
      float r = clamp(cornerRadius, 0.0, 1.0);
      float d = sdRoundBox(p, b, r);                  // negative inside
      mask = 1.0 - smoothstep(-feather, 0.0, d);
    }
    alpha *= mask;

    // ── 6. spill suppression, weighted to the EDGE (partial alpha) — that's
    //       where the key colour has bled onto the subject. Without it you get
    //       a green rim that reads as "pasted on" however good the matte is.
    vec3 rgb = src.rgb;
    if (keyOn > 0.5) {
      float luma = dot(src.rgb, vec3(0.2126, 0.7152, 0.0722));
      rgb = mix(src.rgb, vec3(luma), spill * (1.0 - alpha));
    }

    if (debugKey > 0.5) { gl_FragColor = vec4(vec3(alpha), 1.0); return; }

    // ── 7. grade into the world ───────────────────────────────────────────
    // tint carries the hour's light (hue, brightness-neutral) and the night dim.
    rgb *= tint;
    // Then the same air as everything else. Applied to COLOUR only, never
    // alpha: fog thickens what you see through, it doesn't make a figure
    // transparent. This is what stops a keyed subject reading as a decal
    // pasted in front of a hazy scene.
    if (fogAmount > 0.0) {
      float f = smoothstep(fogNear, fogFar, vFogDepth) * fogAmount;
      rgb = mix(rgb, fogColor, f);
    }

    // Fully-transparent pixels are discarded rather than drawn, so they can't
    // write depth or leave a faint rectangle where the video "is".
    if (alpha < 0.01) discard;
    gl_FragColor = vec4(rgb, alpha * opacity);
  }
`;

/**
 * Build the portal material. Uniforms are mutated in place afterwards —
 * rebuilding it would recompile the shader program and stutter the clip.
 */
export function makePortalMaterial({
  key = false,
  keyColor = KEY_PRESETS.green,
  // Hue tolerance and saturation floor, both normalised against the key's own
  // chroma — so these numbers mean the same thing whichever key colour is used.
  similarity = 0.4,
  satFloor = 0.25,
  smoothness = 0.15,
  spill = 0.35,
  matteChoke = 0.0,
  matteFeather = 0.08,
  shape = SHAPES.rect,
  cornerRadius = 0.0,
  edgeFeather = 0.0,
  quadAspect = 1.0,
  debug = false,
} = {}) {
  return new THREE.ShaderMaterial({
    uniforms: {
      map: { value: null },
      keyOn: { value: key ? 1 : 0 },
      keyColor: { value: new THREE.Color(keyColor) },
      similarity: { value: similarity },
      satFloor: { value: satFloor },
      smoothness: { value: smoothness },
      spill: { value: spill },
      matteChoke: { value: matteChoke },
      matteFeather: { value: matteFeather },
      crop: { value: new THREE.Vector4(0, 0, 0, 0) },
      cropFeather: { value: 0.01 },
      shape: { value: shape },
      cornerRadius: { value: cornerRadius },
      edgeFeather: { value: edgeFeather },
      quadAspect: { value: quadAspect },
      opacity: { value: 0 },
      tint: { value: new THREE.Color(1, 1, 1) },
      fogColor: { value: new THREE.Color(1, 1, 1) },
      fogNear: { value: 80 },
      fogFar: { value: 260 },
      fogAmount: { value: 1 },
      debugKey: { value: debug ? 1 : 0 },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
}
